import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getEmpleados, getCentrosCosto, getCuentas } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calculator, Plus, Eye, Printer, PlayCircle, AlertCircle, DollarSign, CheckCircle2, Clock, UserPlus, Pencil, Trash2, Receipt } from 'lucide-react';

const MESES = [
  { value: '01', label: 'Enero' }, { value: '02', label: 'Febrero' }, { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' }, { value: '05', label: 'Mayo' }, { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' }, { value: '08', label: 'Agosto' }, { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' }, { value: '11', label: 'Noviembre' }, { value: '12', label: 'Diciembre' },
];

const TIPOS_PLANILLA = [
  { value: 'QUINCENAL', label: 'Quincenal' }, { value: 'MENSUAL', label: 'Mensual' },
  { value: 'GRATIFICACION', label: 'Gratificación' }, { value: 'CTS', label: 'CTS' },
  { value: 'LIQUIDACION', label: 'Liquidación' },
];

const MEDIOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' }, { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'DEPOSITO', label: 'Depósito' },
];

const PlanillaPage = () => {
  const { empresaActual } = useApp();
  const [planillas, setPlanillas] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [adelantosPendientes, setAdelantosPendientes] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Modal Nueva Planilla
  const [modalOpen, setModalOpen] = useState(false);
  const [formPlanilla, setFormPlanilla] = useState({
    tipo: 'MENSUAL',
    anio: new Date().getFullYear().toString(),
    mes: (new Date().getMonth() + 1).toString().padStart(2, '0'),
    quincena: '1',
  });
  
  // Modo edición (antes de generar)
  const [modoEdicion, setModoEdicion] = useState(false);
  const [datosEdicion, setDatosEdicion] = useState([]);
  
  // Planilla en visualización
  const [planillaActual, setPlanillaActual] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Modal Pago
  const [modalPagoOpen, setModalPagoOpen] = useState(false);
  const [detalleAPagar, setDetalleAPagar] = useState(null);
  const [formPago, setFormPago] = useState({
    monto: '',
    cuenta_id: '',
    medio_pago: 'TRANSFERENCIA',
    fecha: new Date().toISOString().split('T')[0]
  });
  
  // Modal Agregar Empleado
  const [modalAgregarOpen, setModalAgregarOpen] = useState(false);
  const [formAgregar, setFormAgregar] = useState({
    empleado_id: '',
    neto_pagar: '',
    centro_costo_id: ''
  });
  
  // Modal Editar Empleado
  const [modalEditarOpen, setModalEditarOpen] = useState(false);
  const [detalleAEditar, setDetalleAEditar] = useState(null);
  const [formEditar, setFormEditar] = useState({
    neto_pagar: '',
    centro_costo_id: ''
  });
  
  // Modal Ver Pagos
  const [modalPagosOpen, setModalPagosOpen] = useState(false);
  const [detalleVerPagos, setDetalleVerPagos] = useState(null);
  const [pagosDetalle, setPagosDetalle] = useState([]);
  const [loadingPagos, setLoadingPagos] = useState(false);

  // ============ VER PAGOS DE EMPLEADO ============
  const handleVerPagos = async (detalle) => {
    setDetalleVerPagos(detalle);
    setLoadingPagos(true);
    setModalPagosOpen(true);
    try {
      const response = await api.get(`/planillas/${planillaActual.id}/detalle/${detalle.id}/pagos`);
      setPagosDetalle(response.data);
    } catch (error) {
      toast.error('Error al cargar pagos');
      setPagosDetalle([]);
    } finally {
      setLoadingPagos(false);
    }
  };

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const [empRes, ccRes, cuentasRes, planRes, adelantosRes] = await Promise.all([
        getEmpleados(empresaActual.id, true),
        getCentrosCosto(empresaActual.id),
        getCuentas(empresaActual.id),
        api.get('/planillas', { params: { empresa_id: empresaActual.id } }),
        api.get('/adelantos/pendientes-por-empleado', { params: { empresa_id: empresaActual.id } })
      ]);
      setEmpleados(empRes.data);
      setCentrosCosto(ccRes.data);
      setCuentas(cuentasRes.data);
      setPlanillas(planRes.data);
      setAdelantosPendientes(adelantosRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Verificar si existe planilla para el período seleccionado
  const existePlanillaParaPeriodo = () => {
    const periodo = `${formPlanilla.anio}-${formPlanilla.mes}`;
    const quincena = formPlanilla.tipo === 'QUINCENAL' ? parseInt(formPlanilla.quincena) : 0;
    return planillas.find(p => 
      p.tipo === formPlanilla.tipo && 
      p.periodo === periodo && 
      p.quincena === quincena
    );
  };

  // ============ NUEVA PLANILLA ============
  const handleNuevaPlanilla = () => {
    setFormPlanilla({
      tipo: 'QUINCENAL', // Default a Quincenal
      anio: new Date().getFullYear().toString(),
      mes: (new Date().getMonth() + 1).toString().padStart(2, '0'),
      quincena: '1',
    });
    setModalOpen(true);
  };

  const handleIniciarEdicion = () => {
    if (empleados.length === 0) { toast.error('No hay empleados activos'); return; }
    
    // Verificar duplicado
    const existente = existePlanillaParaPeriodo();
    if (existente) {
      const quincenaText = existente.quincena > 0 ? ` Q${existente.quincena}` : '';
      toast.error(`Ya existe planilla ${existente.tipo} para ${existente.periodo}${quincenaText}`);
      return;
    }
    
    const datos = empleados.map(emp => {
      const adelanto = adelantosPendientes[emp.id] || { total: 0, cantidad: 0 };
      return {
        empleado_id: emp.id, empleado: emp,
        centro_costo_id: emp.centro_costo_id?.toString() || '',
        monto_pagar: '',
        adelanto_pendiente: adelanto.total,
        num_adelantos: adelanto.cantidad,
        forma_pago: emp.forma_pago || 'BANCO',
      };
    });
    setDatosEdicion(datos);
    setModalOpen(false);
    setModoEdicion(true);
  };

  const handleUpdateDatosEdicion = (empleadoId, campo, valor) => {
    setDatosEdicion(prev => prev.map(d => d.empleado_id === empleadoId ? { ...d, [campo]: valor } : d));
  };

  const calcularNetoFinal = (dato) => (parseFloat(dato.monto_pagar) || 0) - (dato.adelanto_pendiente || 0);

  const totalesEdicion = {
    montoPagar: datosEdicion.reduce((s, d) => s + (parseFloat(d.monto_pagar) || 0), 0),
    adelantos: datosEdicion.reduce((s, d) => s + (d.adelanto_pendiente || 0), 0),
    netoFinal: datosEdicion.reduce((s, d) => s + calcularNetoFinal(d), 0),
  };

  const handleGenerarPlanilla = async () => {
    const sinMonto = datosEdicion.filter(d => !d.monto_pagar || parseFloat(d.monto_pagar) <= 0);
    if (sinMonto.length > 0) { toast.error(`Ingrese el monto para todos (${sinMonto.length} sin monto)`); return; }

    setSaving(true);
    try {
      const periodo = `${formPlanilla.anio}-${formPlanilla.mes}`;
      const response = await api.post('/planillas', {
        empresa_id: empresaActual.id, tipo: formPlanilla.tipo, periodo,
        quincena: formPlanilla.tipo === 'QUINCENAL' ? parseInt(formPlanilla.quincena) : 0,
        fecha_pago: new Date().toISOString().split('T')[0],
        total_ingresos: totalesEdicion.montoPagar,
        total_descuentos: totalesEdicion.adelantos,
        total_aportes: 0,
        total_neto: totalesEdicion.netoFinal,
        detalles: datosEdicion.map(d => ({
          empleado_id: d.empleado_id, dias_trabajados: 30,
          sueldo_basico: parseFloat(d.monto_pagar) || 0,
          total_ingresos: parseFloat(d.monto_pagar) || 0,
          total_descuentos: d.adelanto_pendiente || 0,
          total_aportes: 0, neto_pagar: calcularNetoFinal(d),
          forma_pago: d.forma_pago, banco_id: d.empleado.banco_id,
          nro_cuenta: d.empleado.nro_cuenta,
          centro_costo_id: d.centro_costo_id ? parseInt(d.centro_costo_id) : null,
          linea_negocio_id: d.empleado.linea_negocio_id,
        }))
      });
      
      // Marcar adelantos como aplicados
      for (const dato of datosEdicion) {
        if (dato.adelanto_pendiente > 0) {
          await api.put(`/adelantos/aplicar-por-empleado/${dato.empleado_id}`, null, { params: { planilla_id: response.data.id } });
        }
      }
      
      toast.success('Planilla generada correctamente');
      setModoEdicion(false);
      loadData();
      
      const planillaCreada = await api.get(`/planillas/${response.data.id}`);
      setPlanillaActual(planillaCreada.data);
      setDetalles(planillaCreada.data.detalles || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar planilla');
    } finally {
      setSaving(false);
    }
  };

  // ============ VER PLANILLA ============
  const handleVerPlanilla = async (planilla) => {
    try {
      const response = await api.get(`/planillas/${planilla.id}`);
      setPlanillaActual(response.data);
      setDetalles(response.data.detalles || []);
    } catch (error) {
      toast.error('Error al cargar planilla');
    }
  };

  // ============ AGREGAR EMPLEADO ============
  const empleadosNoEnPlanilla = () => {
    const idsEnPlanilla = detalles.map(d => d.empleado_id);
    return empleados.filter(e => !idsEnPlanilla.includes(e.id));
  };

  const handleAbrirModalAgregar = () => {
    setFormAgregar({ empleado_id: '', neto_pagar: '', centro_costo_id: '' });
    setModalAgregarOpen(true);
  };

  const handleAgregarEmpleado = async () => {
    if (!formAgregar.empleado_id || !formAgregar.neto_pagar) {
      toast.error('Seleccione empleado e ingrese el monto');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/planillas/${planillaActual.id}/agregar-empleado`, {
        empleado_id: parseInt(formAgregar.empleado_id),
        neto_pagar: parseFloat(formAgregar.neto_pagar),
        centro_costo_id: formAgregar.centro_costo_id ? parseInt(formAgregar.centro_costo_id) : null
      });
      toast.success('Empleado agregado correctamente');
      setModalAgregarOpen(false);
      // Recargar planilla
      const response = await api.get(`/planillas/${planillaActual.id}`);
      setPlanillaActual(response.data);
      setDetalles(response.data.detalles || []);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar empleado');
    } finally {
      setSaving(false);
    }
  };

  // ============ EDITAR EMPLEADO ============
  const handleAbrirModalEditar = (detalle) => {
    setDetalleAEditar(detalle);
    setFormEditar({
      neto_pagar: detalle.neto_pagar?.toString() || '',
      centro_costo_id: detalle.centro_costo_id?.toString() || ''
    });
    setModalEditarOpen(true);
  };

  const handleEditarEmpleado = async () => {
    if (!formEditar.neto_pagar) {
      toast.error('Ingrese el monto');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/planillas/${planillaActual.id}/empleado/${detalleAEditar.id}`, {
        neto_pagar: parseFloat(formEditar.neto_pagar),
        centro_costo_id: formEditar.centro_costo_id ? parseInt(formEditar.centro_costo_id) : null
      });
      toast.success('Empleado actualizado');
      setModalEditarOpen(false);
      const response = await api.get(`/planillas/${planillaActual.id}`);
      setPlanillaActual(response.data);
      setDetalles(response.data.detalles || []);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  // ============ ELIMINAR EMPLEADO ============
  const handleEliminarEmpleado = async (detalle) => {
    const confirmacion = window.confirm(`¿Está seguro de eliminar a ${detalle.apellido_paterno} ${detalle.nombres} de la planilla?`);
    if (!confirmacion) {
      return;
    }
    
    try {
      await api.delete(`/planillas/${planillaActual.id}/empleado/${detalle.id}`);
      toast.success('Empleado eliminado de la planilla');
      const response = await api.get(`/planillas/${planillaActual.id}`);
      setPlanillaActual(response.data);
      setDetalles(response.data.detalles || []);
      loadData();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // ============ PAGAR EMPLEADO ============
  const handleAbrirModalPago = (detalle) => {
    const saldoPendiente = (detalle.neto_pagar || 0) - (detalle.monto_pagado || 0);
    setDetalleAPagar(detalle);
    setFormPago({
      monto: saldoPendiente.toFixed(2),
      cuenta_id: cuentas.length > 0 ? cuentas[0].id.toString() : '',
      medio_pago: detalle.forma_pago === 'EFECTIVO' ? 'EFECTIVO' : 'TRANSFERENCIA',
      fecha: new Date().toISOString().split('T')[0]
    });
    setModalPagoOpen(true);
  };

  const handleRegistrarPago = async () => {
    if (!formPago.monto || !formPago.cuenta_id) { toast.error('Complete los campos'); return; }
    
    setSaving(true);
    try {
      await api.post(`/planillas/${planillaActual.id}/pagar-empleado`, {
        detalle_id: detalleAPagar.id,
        monto: parseFloat(formPago.monto),
        cuenta_id: parseInt(formPago.cuenta_id),
        medio_pago: formPago.medio_pago,
        fecha: formPago.fecha
      });
      toast.success('Pago registrado correctamente');
      setModalPagoOpen(false);
      
      // Recargar planilla
      const response = await api.get(`/planillas/${planillaActual.id}`);
      setPlanillaActual(response.data);
      setDetalles(response.data.detalles || []);
      loadData(); // Recargar lista de planillas por si cambió el estado
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setSaving(false);
    }
  };

  // ============ TOTALES ============
  const totales = {
    bruto: detalles.reduce((s, d) => s + (d.neto_pagar || 0) + (d.total_descuentos || 0), 0),
    adelantos: detalles.reduce((s, d) => s + (d.total_descuentos || 0), 0),
    neto: detalles.reduce((s, d) => s + (d.neto_pagar || 0), 0),
    pagado: detalles.reduce((s, d) => s + (d.monto_pagado || 0), 0),
    pendiente: detalles.reduce((s, d) => s + ((d.neto_pagar || 0) - (d.monto_pagado || 0)), 0),
  };

  return (
    <div className="space-y-6" data-testid="planilla-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="w-7 h-7 text-orange-600" />
            Planilla de Remuneraciones
          </h1>
          <p className="text-gray-500 text-sm mt-1">Generación y gestión de planillas</p>
        </div>
        <Button onClick={handleNuevaPlanilla} className="bg-orange-600 hover:bg-orange-700">
          <Plus className="w-4 h-4 mr-2" /> Nueva Planilla
        </Button>
      </div>

      {/* ============ MODO EDICIÓN ============ */}
      {modoEdicion ? (
        <div className="space-y-4">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-blue-100 text-blue-700">{formPlanilla.tipo}</Badge>
                    <h2 className="text-xl font-bold text-gray-900">
                      Nueva Planilla: {formPlanilla.anio}-{formPlanilla.mes}
                      {formPlanilla.tipo === 'QUINCENAL' && ` - Q${formPlanilla.quincena}`}
                    </h2>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Ingrese el monto a pagar para cada empleado</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setModoEdicion(false)}>Cancelar</Button>
                  <Button onClick={handleGenerarPlanilla} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                    <PlayCircle className="w-4 h-4 mr-2" /> {saving ? 'Generando...' : 'Confirmar y Generar'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500">Total a Pagar</p>
                <p className="text-2xl font-bold text-emerald-600">S/. {totalesEdicion.montoPagar.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500">Adelantos</p>
                <p className="text-2xl font-bold text-amber-600">S/. {totalesEdicion.adelantos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-800">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-300">Neto Final</p>
                <p className="text-2xl font-bold text-white">S/. {totalesEdicion.netoFinal.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Empleado</TableHead>
                    <TableHead>Sueldo Ref.</TableHead>
                    <TableHead>Centro de Costo</TableHead>
                    <TableHead className="text-center">Adelantos</TableHead>
                    <TableHead className="text-center w-40">Monto a Pagar *</TableHead>
                    <TableHead className="text-right">Neto Final</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {datosEdicion.map((dato) => (
                    <TableRow key={dato.empleado_id}>
                      <TableCell>
                        <div className="font-medium">{dato.empleado?.apellido_paterno} {dato.empleado?.apellido_materno}</div>
                        <div className="text-xs text-gray-500">{dato.empleado?.nombres} - {dato.empleado?.cargo}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">Real: S/. {dato.empleado?.sueldo_real?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</div>
                        <div className="text-xs text-gray-400">Planilla: S/. {dato.empleado?.sueldo_planilla?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</div>
                      </TableCell>
                      <TableCell>
                        <Select value={dato.centro_costo_id || "none"} onValueChange={(v) => handleUpdateDatosEdicion(dato.empleado_id, 'centro_costo_id', v === "none" ? "" : v)}>
                          <SelectTrigger className="w-36 h-8 text-sm"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin asignar</SelectItem>
                            {centrosCosto.map(cc => <SelectItem key={cc.id} value={cc.id.toString()}>{cc.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-center">
                        {dato.adelanto_pendiente > 0 ? (
                          <span className="text-amber-600 font-medium">S/. {dato.adelanto_pendiente.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                        ) : <span className="text-gray-400">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <Input type="number" min="0" step="0.01" value={dato.monto_pagar}
                          onChange={(e) => handleUpdateDatosEdicion(dato.empleado_id, 'monto_pagar', e.target.value)}
                          className="w-32 h-8 text-right text-sm" placeholder="0.00" />
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {calcularNetoFinal(dato) > 0 ? `S/. ${calcularNetoFinal(dato).toLocaleString('es-PE', { minimumFractionDigits: 2 })}` : <span className="text-gray-400">-</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      ) : planillaActual ? (
        /* ============ VISTA DE PLANILLA ============ */
        <div className="space-y-4">
          <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-orange-100 text-orange-700">{planillaActual.tipo}</Badge>
                    <h2 className="text-xl font-bold text-gray-900">
                      Período: {planillaActual.periodo}
                      {planillaActual.quincena > 0 && ` Q${planillaActual.quincena}`}
                    </h2>
                    <Badge className={
                      planillaActual.estado === 'CALCULADA' ? 'bg-blue-100 text-blue-700' :
                      planillaActual.estado === 'PAGADA' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                    }>{planillaActual.estado}</Badge>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{detalles.length} empleados</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPlanillaActual(null)}>← Volver</Button>
                  {empleadosNoEnPlanilla().length > 0 && (
                    <Button size="sm" onClick={handleAbrirModalAgregar} className="bg-blue-600 hover:bg-blue-700">
                      <UserPlus className="w-4 h-4 mr-1" /> Agregar Empleado
                    </Button>
                  )}
                  <Button variant="outline" size="sm"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resumen con estado de pago */}
          <div className="grid grid-cols-5 gap-4">
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500">Monto Bruto</p>
                <p className="text-xl font-bold text-gray-600">S/. {totales.bruto.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500">Adelantos</p>
                <p className="text-xl font-bold text-amber-600">- S/. {totales.adelantos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500">Neto a Pagar</p>
                <p className="text-xl font-bold text-blue-600">S/. {totales.neto.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500 flex items-center justify-center gap-1"><CheckCircle2 className="w-4 h-4" /> Pagado</p>
                <p className="text-xl font-bold text-emerald-600">S/. {totales.pagado.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </CardContent>
            </Card>
            <Card className={totales.pendiente > 0 ? "bg-red-50 border-red-200" : "bg-gray-100"}>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500 flex items-center justify-center gap-1"><Clock className="w-4 h-4" /> Pendiente</p>
                <p className={`text-xl font-bold ${totales.pendiente > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  S/. {totales.pendiente.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Tabla con botón de pago */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empleado</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead className="text-right">Monto Bruto</TableHead>
                    <TableHead className="text-right text-amber-700">Adelantos</TableHead>
                    <TableHead className="text-right">Neto a Pagar</TableHead>
                    <TableHead className="text-right text-emerald-700">Pagado</TableHead>
                    <TableHead className="text-right text-red-700">Pendiente</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detalles.map((det) => {
                    const adelantos = det.total_descuentos || 0;
                    const montoBruto = (det.neto_pagar || 0) + adelantos;
                    const pendiente = (det.neto_pagar || 0) - (det.monto_pagado || 0);
                    const estaPagado = pendiente < 0.01;
                    const tienePagos = (det.monto_pagado > 0) || (det.num_pagos > 0);
                    return (
                      <TableRow key={det.id} className={estaPagado ? 'bg-emerald-50/50' : ''}>
                        <TableCell>
                          <div className="font-medium">{det.apellido_paterno} {det.apellido_materno}</div>
                          <div className="text-sm text-gray-500">{det.nombres}</div>
                        </TableCell>
                        <TableCell className="text-sm">{det.cargo || '-'}</TableCell>
                        <TableCell className="text-right font-mono text-gray-600">
                          S/. {montoBruto.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {adelantos > 0 ? (
                            <span className="text-amber-600 font-medium">- S/. {adelantos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                          ) : <span className="text-gray-400">-</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          S/. {det.neto_pagar?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">
                          {det.monto_pagado > 0 ? `S/. ${det.monto_pagado?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {pendiente > 0.01 ? (
                            <span className="text-red-600">S/. {pendiente.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                          ) : <span className="text-gray-400">-</span>}
                        </TableCell>
                        <TableCell>
                          {estaPagado ? (
                            <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Pagado</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Botón ver pagos si tiene pagos */}
                            {tienePagos && (
                              <Button variant="ghost" size="sm" onClick={() => handleVerPagos(det)} title="Ver pagos">
                                <Receipt className="w-4 h-4 text-purple-500" />
                              </Button>
                            )}
                            {/* Botones editar/eliminar solo si no tiene pagos */}
                            {!tienePagos && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleAbrirModalEditar(det)} title="Editar monto">
                                  <Pencil className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEliminarEmpleado(det); }} title="Eliminar de planilla">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </>
                            )}
                            {/* Botón pagar si tiene saldo pendiente */}
                            {!estaPagado && (
                              <Button size="sm" onClick={() => handleAbrirModalPago(det)} className="bg-emerald-600 hover:bg-emerald-700">
                                <DollarSign className="w-4 h-4 mr-1" /> Pagar
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* ============ LISTA DE PLANILLAS ============ */
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Cargando...</div>
            ) : planillas.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Calculator className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No hay planillas generadas</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Período</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Total Neto</TableHead>
                    <TableHead className="text-right">Pagado</TableHead>
                    <TableHead className="text-right">Pendiente</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {planillas.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.periodo}{p.quincena > 0 && <span className="text-gray-500 ml-1">Q{p.quincena}</span>}</TableCell>
                      <TableCell><Badge variant="outline">{p.tipo}</Badge></TableCell>
                      <TableCell>
                        <Badge className={
                          p.estado === 'CALCULADA' ? 'bg-blue-100 text-blue-700' :
                          p.estado === 'PAGADA' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                        }>{p.estado}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">S/. {(p.total_neto || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">
                        {p.total_pagado > 0 ? `S/. ${p.total_pagado.toLocaleString('es-PE', { minimumFractionDigits: 2 })}` : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {p.total_pendiente > 0.01 ? (
                          <span className="text-red-600 font-bold">S/. {p.total_pendiente.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                        ) : <span className="text-gray-400">-</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleVerPlanilla(p)}><Eye className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ MODAL NUEVA PLANILLA ============ */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-orange-600" />Nueva Planilla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Tipo de Planilla</Label>
              <Select value={formPlanilla.tipo} onValueChange={(v) => setFormPlanilla({...formPlanilla, tipo: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_PLANILLA.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Año</Label>
                <Input type="number" value={formPlanilla.anio} onChange={(e) => setFormPlanilla({...formPlanilla, anio: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Mes</Label>
                <Select value={formPlanilla.mes} onValueChange={(v) => setFormPlanilla({...formPlanilla, mes: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{MESES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {formPlanilla.tipo === 'QUINCENAL' && (
              <div>
                <Label>Quincena</Label>
                <Select value={formPlanilla.quincena} onValueChange={(v) => setFormPlanilla({...formPlanilla, quincena: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Primera (1-15)</SelectItem>
                    <SelectItem value="2">Segunda (16-30)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Advertencia si ya existe planilla */}
            {existePlanillaParaPeriodo() && (
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Ya existe una planilla {formPlanilla.tipo} para {formPlanilla.anio}-{formPlanilla.mes}
                  {formPlanilla.tipo === 'QUINCENAL' && ` Q${formPlanilla.quincena}`}
                </p>
                <p className="text-xs text-red-600 mt-1">Seleccione otro período o quincena</p>
              </div>
            )}
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600"><strong>Empleados activos:</strong> {empleados.length}</p>
              {Object.keys(adelantosPendientes).length > 0 && (
                <p className="text-sm text-amber-600 mt-1"><AlertCircle className="w-4 h-4 inline mr-1" />Hay adelantos pendientes</p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleIniciarEdicion} 
              disabled={empleados.length === 0 || existePlanillaParaPeriodo()} 
              className="bg-orange-600 hover:bg-orange-700"
            >
              <PlayCircle className="w-4 h-4 mr-2" />Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL PAGAR EMPLEADO ============ */}
      <Dialog open={modalPagoOpen} onOpenChange={setModalPagoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600" />Registrar Pago</DialogTitle>
          </DialogHeader>
          {detalleAPagar && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium">{detalleAPagar.apellido_paterno} {detalleAPagar.apellido_materno}, {detalleAPagar.nombres}</p>
                <p className="text-sm text-gray-500">{detalleAPagar.cargo}</p>
                <div className="mt-2 flex justify-between text-sm">
                  <span>Neto a pagar:</span>
                  <span className="font-bold">S/. {detalleAPagar.neto_pagar?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Ya pagado:</span>
                  <span className="text-emerald-600">S/. {(detalleAPagar.monto_pagado || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm font-bold border-t pt-2 mt-2">
                  <span>Saldo pendiente:</span>
                  <span className="text-red-600">S/. {((detalleAPagar.neto_pagar || 0) - (detalleAPagar.monto_pagado || 0)).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monto a Pagar *</Label>
                  <Input type="number" min="0" step="0.01" value={formPago.monto} onChange={(e) => setFormPago({...formPago, monto: e.target.value})} className="mt-1 text-right" />
                </div>
                <div>
                  <Label>Fecha</Label>
                  <Input type="date" value={formPago.fecha} onChange={(e) => setFormPago({...formPago, fecha: e.target.value})} className="mt-1" />
                </div>
              </div>
              
              <div>
                <Label>Cuenta de Pago *</Label>
                <Select value={formPago.cuenta_id} onValueChange={(v) => setFormPago({...formPago, cuenta_id: v})}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar cuenta..." /></SelectTrigger>
                  <SelectContent>{cuentas.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Medio de Pago</Label>
                <Select value={formPago.medio_pago} onValueChange={(v) => setFormPago({...formPago, medio_pago: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{MEDIOS_PAGO.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-sm text-blue-700">💡 Puedes hacer pagos parciales. Por ejemplo, parte en efectivo y parte por banco.</p>
              </div>
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalPagoOpen(false)}>Cancelar</Button>
            <Button onClick={handleRegistrarPago} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? 'Registrando...' : 'Registrar Pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL AGREGAR EMPLEADO ============ */}
      <Dialog open={modalAgregarOpen} onOpenChange={setModalAgregarOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-blue-600" />Agregar Empleado a Planilla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Empleado *</Label>
              <Select value={formAgregar.empleado_id} onValueChange={(v) => setFormAgregar({...formAgregar, empleado_id: v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar empleado..." /></SelectTrigger>
                <SelectContent>
                  {empleadosNoEnPlanilla().map(e => (
                    <SelectItem key={e.id} value={e.id.toString()}>
                      {e.apellido_paterno} {e.apellido_materno}, {e.nombres} - {e.cargo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monto a Pagar (Neto) *</Label>
              <Input type="number" min="0" step="0.01" value={formAgregar.neto_pagar} 
                onChange={(e) => setFormAgregar({...formAgregar, neto_pagar: e.target.value})} 
                className="mt-1 text-right" placeholder="0.00" />
            </div>
            <div>
              <Label>Centro de Costo</Label>
              <Select value={formAgregar.centro_costo_id || "none"} onValueChange={(v) => setFormAgregar({...formAgregar, centro_costo_id: v === "none" ? "" : v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {centrosCosto.map(cc => <SelectItem key={cc.id} value={cc.id.toString()}>{cc.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalAgregarOpen(false)}>Cancelar</Button>
            <Button onClick={handleAgregarEmpleado} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Agregando...' : 'Agregar a Planilla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL EDITAR EMPLEADO ============ */}
      <Dialog open={modalEditarOpen} onOpenChange={setModalEditarOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-blue-600" />Editar Monto</DialogTitle>
          </DialogHeader>
          {detalleAEditar && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium">{detalleAEditar.apellido_paterno} {detalleAEditar.apellido_materno}, {detalleAEditar.nombres}</p>
                <p className="text-sm text-gray-500">{detalleAEditar.cargo}</p>
              </div>
              <div>
                <Label>Monto a Pagar (Neto) *</Label>
                <Input type="number" min="0" step="0.01" value={formEditar.neto_pagar} 
                  onChange={(e) => setFormEditar({...formEditar, neto_pagar: e.target.value})} 
                  className="mt-1 text-right" />
              </div>
              <div>
                <Label>Centro de Costo</Label>
                <Select value={formEditar.centro_costo_id || "none"} onValueChange={(v) => setFormEditar({...formEditar, centro_costo_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {centrosCosto.map(cc => <SelectItem key={cc.id} value={cc.id.toString()}>{cc.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <p className="text-sm text-amber-700">⚠️ Solo se puede editar si no se han registrado pagos para este empleado.</p>
              </div>
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalEditarOpen(false)}>Cancelar</Button>
            <Button onClick={handleEditarEmpleado} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL VER PAGOS EMPLEADO ============ */}
      <Dialog open={modalPagosOpen} onOpenChange={setModalPagosOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-purple-600" />Pagos Vinculados</DialogTitle>
          </DialogHeader>
          {detalleVerPagos && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium">{detalleVerPagos.apellido_paterno} {detalleVerPagos.apellido_materno}, {detalleVerPagos.nombres}</p>
                <p className="text-sm text-gray-500">{detalleVerPagos.cargo}</p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Neto:</span>
                    <span className="font-bold ml-1">S/. {detalleVerPagos.neto_pagar?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Pagado:</span>
                    <span className="font-bold text-emerald-600 ml-1">S/. {(detalleVerPagos.monto_pagado || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Pendiente:</span>
                    <span className="font-bold text-red-600 ml-1">S/. {((detalleVerPagos.neto_pagar || 0) - (detalleVerPagos.monto_pagado || 0)).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
              
              {loadingPagos ? (
                <div className="text-center py-8 text-gray-500">Cargando pagos...</div>
              ) : pagosDetalle.length === 0 ? (
                <div className="text-center py-8 text-gray-400">No hay pagos registrados</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead>Cuenta</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagosDetalle.map((pago, idx) => (
                      <TableRow key={pago.id || idx}>
                        <TableCell className="text-sm">{pago.fecha}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{pago.medio_pago}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">{pago.cuenta_nombre}</TableCell>
                        <TableCell className="text-right font-mono font-bold text-emerald-600">
                          S/. {pago.monto?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <p className="text-sm text-purple-700">💡 Estos pagos también se reflejan en Tesorería → Pagos.</p>
              </div>
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalPagosOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanillaPage;
